import React, { useEffect, useState } from "react";
import { Activity, Crown, Briefcase, Users, ShieldCheck, Bus, Truck } from "lucide-react";
import { fetchTeamActivity } from "../lib/api";

const ROLE_ICON = {
  admin:      { Icon: Crown,       cls: "text-rose-300" },
  dispatcher: { Icon: Briefcase,   cls: "text-amber-300" },
  sales:      { Icon: Users,       cls: "text-emerald-300" },
  lead_host:  { Icon: ShieldCheck, cls: "text-violet-300" },
  host:       { Icon: Bus,         cls: "text-cyan-300" },
  driver:     { Icon: Truck,       cls: "text-orange-300" },
};

// Map entity_type → short human label for the breakdown chips.
const ENTITY_LABEL = {
  dispensary:          "edits",
  bus_tasting:         "bus events",
  interaction:         "activity entries",
  task:                "tasks",
  contact:             "team contacts",
  dispensary_contact:  "people",
  sales_note:          "notes",
  scan:                "scans",
  user:                "user changes",
};

function describeBreakdown(byEntity) {
  return Object.entries(byEntity)
    .map(([k, v]) => `${v} ${ENTITY_LABEL[k] || k}`)
    .join(" · ");
}

/**
 * Admin-only dashboard tile. "What did the team do this week?"
 * Sources from /api/admin/team-activity which aggregates the audit log
 * by user over the last N days (default 7).
 */
export function TeamActivityCard({ days = 7 }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    fetchTeamActivity(days)
      .then(setRows)
      .catch(() => setRows([]));
  }, [days]);

  return (
    <div className="panel p-5" data-testid="team-activity-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-zinc-100">
            Team activity
          </h3>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          Last {days} days
        </span>
      </div>

      {rows === null ? (
        <div className="text-xs text-zinc-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-3">
          No team activity recorded in this window. Activity will appear
          here as reps log calls, complete tasks, and update accounts.
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="team-activity-list">
          {rows.map((r) => {
            const meta = ROLE_ICON[r.role] || ROLE_ICON.sales;
            const RoleIcon = meta.Icon;
            return (
              <li
                key={r.user_email}
                data-testid={`team-activity-${r.user_email}`}
                className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2.5"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <RoleIcon className={`w-4 h-4 ${meta.cls} shrink-0`} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-100 truncate">
                      {r.user_name}
                    </div>
                    <div className="text-[11px] text-zinc-500 leading-tight truncate">
                      {describeBreakdown(r.by_entity) || "—"}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-base font-mono font-semibold text-emerald-300">
                    {r.total}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">
                    actions
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default TeamActivityCard;
